import React, { useCallback, useEffect, useState } from 'react';
import usePageTitle from '../hooks/usePageTitle';
import { createPortal } from 'react-dom';
import { useSearchParams } from 'react-router-dom';
import { toast } from 'react-toastify';
import { axiosInstance } from '../routes/axiosInstance';
import MarkdownRenderer from '../components/MarkdownRenderer';
import {useBreadcrumbs} from '../contexts/BreadcrumbContext';
import { SLIDE_OVER_TOP_OFFSET_STYLE } from '../components/slideOverTransition';
import {
  BookOpenIcon,
  HomeIcon,
  CodeBracketIcon,
  XMarkIcon,
  Bars3Icon,
} from '@heroicons/react/24/outline';

const ADMIN_DOCS_NAVIGATION = [
  {
    title: 'Getting Started',
    icon: HomeIcon,
    items: [
      { title: 'Home', page: 'index' },
      { title: 'Getting Started', page: 'getting_started' },
      { title: 'Authentication', page: 'authentication' },
    ],
  },
  {
    title: 'Market Owner Interactions',
    icon: CodeBracketIcon,
    items: [
      { title: 'Viewing Available Resources', page: 'available_resources' },
      { title: 'Viewing Ensemble Forecasts', page: 'ensemble_forecasts' },
      { title: 'Viewing Individual Forecasts', page: 'individual_forecasts' },
      { title: 'Viewing Raw Data', page: 'raw_data' },
    ],
  },
];

const DOCS_CONFIG = {
  admin: {
    title: 'Admin Documentation',
    breadcrumb: 'Admin Documentation',
    description: 'Market owner API guides and integration examples',
    endpointBase: '/user/admin-docs',
    routeBase: '/admin-documentation',
    navigation: ADMIN_DOCS_NAVIGATION,
    externalLink: null,
    swaggerUrl: '/swagger/market-owner/',
  },
};

export default function Documentation({ mode = 'admin' }) {
  usePageTitle('Documentation');
  const docsConfig = DOCS_CONFIG[mode] || DOCS_CONFIG.admin;
  useBreadcrumbs(
    [{label: 'Help'}, {label: docsConfig.breadcrumb}],
    docsConfig.description
  );
  const [searchParams, setSearchParams] = useSearchParams();
  const [content, setContent] = useState('');
  const [loading, setLoading] = useState(false);
  const [currentPage, setCurrentPage] = useState(searchParams.get('page') || 'index');
  const [isTocPanelOpen, setIsTocPanelOpen] = useState(false);

  const fetchDocContent = useCallback(async (page) => {
    setLoading(true);
    try {
      const response = await axiosInstance.get(`${docsConfig.endpointBase}/?page=${page}`);
      setContent(response.data.data.content);
    } catch (error) {
      console.error('Failed to load documentation:', error);
      toast.error(`Failed to load ${page}`);
      setContent(`# Documentation Not Found

The requested documentation page could not be loaded.

Please try another page from the navigation.`);
    } finally {
      setLoading(false);
    }
  }, [docsConfig.endpointBase]);

  useEffect(() => {
    const page = searchParams.get('page') || 'index';
    setCurrentPage(page);
    fetchDocContent(page);
  }, [fetchDocContent, searchParams]);

  // Prevent body scroll when TOC panel is open
  useEffect(() => {
    if (isTocPanelOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = 'unset';
    }
    return () => {
      document.body.style.overflow = 'unset';
    };
  }, [isTocPanelOpen]);

  const handlePageChange = (page) => {
    setSearchParams({ page });
    setIsTocPanelOpen(false); // Close panel on mobile after selection
  };

  return (
    <div className="space-y-6">
      {/* Mobile TOC button */}
      <div className="flex justify-end lg:hidden">
        <button
          onClick={() => setIsTocPanelOpen(true)}
          className="flex items-center justify-center p-2 rounded-md border border-gray-300 dark:border-gray-600 bg-surface dark:bg-gray-870 text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700"
          title="Table of Contents"
        >
          <Bars3Icon className="h-6 w-6" />
        </button>
      </div>

      {/* TOC Side Panel (Mobile) - Using Portal */}
      {isTocPanelOpen && createPortal(
        <>
          {/* Backdrop */}
          <div
            className="fixed inset-0 bg-black/50 z-[90]"
            onClick={() => setIsTocPanelOpen(false)}
          />

          {/* Side Panel */}
          <div className="fixed inset-y-0 right-0 w-full sm:w-96 bg-white dark:bg-gray-870 shadow-xl z-[100] overflow-y-auto" style={SLIDE_OVER_TOP_OFFSET_STYLE}>
            {/* Panel Header */}
            <div className="sticky top-0 bg-surface dark:bg-gray-870 border-b border-gray-200/70 dark:border-gray-600/70 px-6 py-4 flex items-center justify-between">
              <h2 className="text-lg font-semibold text-gray-900 dark:text-gray-100 flex items-center gap-2">
                <BookOpenIcon className="w-5 h-5" />
                Table of Contents
              </h2>
              <button
                onClick={() => setIsTocPanelOpen(false)}
                className="p-2 rounded-md hover:bg-gray-100 dark:hover:bg-gray-700 text-gray-500 dark:text-gray-400"
              >
                <XMarkIcon className="h-6 w-6" />
              </button>
            </div>

            {/* Panel Content */}
            <div className="px-6 py-4">
              <nav className="space-y-4">
                {docsConfig.navigation.map((section, idx) => (
                  <div key={idx}>
                    <div className="flex items-center gap-2 text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider mb-2">
                      <section.icon className="w-4 h-4" />
                      {section.title}
                    </div>
                    <ul className="space-y-1">
                      {section.items.map((item) => (
                        <li key={item.page}>
                          <button
                            onClick={() => handlePageChange(item.page)}
                            className={`w-full text-left px-3 py-2 text-sm rounded-md transition-colors ${
                              currentPage === item.page
                                ? 'bg-brand-50 dark:bg-brand-900/30 text-brand-600 dark:text-brand-400 font-medium'
                                : 'text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700/50'
                            }`}
                          >
                            {item.title}
                          </button>
                        </li>
                      ))}
                    </ul>
                  </div>
                ))}
              </nav>

              {/* External Link */}
              {docsConfig.externalLink && (
                <div className="mt-6 pt-4 border-t border-gray-200/70 dark:border-gray-600/70">
                  <a
                    href={docsConfig.externalLink}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex items-center gap-2 text-xs text-brand-600 dark:text-brand-400 hover:text-brand-800 dark:hover:text-brand-300"
                  >
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" />
                    </svg>
                    External Docs Site
                  </a>
                </div>
              )}
            </div>
          </div>
        </>,
        document.body
      )}

      {/* 2-Column Layout */}
      <div className="grid grid-cols-1 lg:grid-cols-4 gap-6">
        {/* Left Sidebar - Navigation (Desktop only) */}
        <div className="hidden lg:block lg:col-span-1">
          <div className="bg-surface dark:bg-gray-870 rounded-lg shadow-sm border border-gray-200/70 dark:border-gray-600/70 p-4 sticky top-4">
            <h2 className="text-sm font-semibold text-gray-900 dark:text-gray-100 mb-4 flex items-center gap-2">
              <BookOpenIcon className="w-5 h-5" />
              Table of Contents
            </h2>

            <nav className="space-y-4">
              {docsConfig.navigation.map((section, idx) => (
                <div key={idx}>
                  <div className="flex items-center gap-2 text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider mb-2">
                    <section.icon className="w-4 h-4" />
                    {section.title}
                  </div>
                  <ul className="space-y-1">
                    {section.items.map((item) => (
                      <li key={item.page}>
                        <button
                          onClick={() => handlePageChange(item.page)}
                          className={`w-full text-left px-3 py-2 text-sm rounded-md transition-colors ${
                            currentPage === item.page
                              ? 'bg-brand-50 dark:bg-brand-900/30 text-brand-600 dark:text-brand-400 font-medium'
                              : 'text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700/50'
                          }`}
                        >
                          {item.title}
                        </button>
                      </li>
                    ))}
                  </ul>
                </div>
              ))}
            </nav>

            {/* External Link */}
            {(docsConfig.externalLink || docsConfig.swaggerUrl) && (
              <div className="mt-6 pt-4 border-t border-gray-200/70 dark:border-gray-600/70 space-y-2">
                {docsConfig.swaggerUrl && (
                  <a
                    href={docsConfig.swaggerUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex items-center gap-2 text-xs text-brand-600 dark:text-brand-400 hover:text-brand-800 dark:hover:text-brand-300"
                  >
                    <CodeBracketIcon className="w-4 h-4" />
                    Open Swagger UI
                  </a>
                )}
                {docsConfig.externalLink && (
                  <a
                    href={docsConfig.externalLink}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex items-center gap-2 text-xs text-brand-600 dark:text-brand-400 hover:text-brand-800 dark:hover:text-brand-300"
                  >
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" />
                    </svg>
                    External Docs Site
                  </a>
                )}
              </div>
            )}
          </div>
        </div>

        {/* Right Content - Documentation */}
        <div className="col-span-12 lg:col-span-3">
          {loading ? (
            <div className="lg:bg-surface lg:dark:bg-gray-870 lg:rounded-lg lg:shadow-sm lg:border lg:border-gray-100 lg:dark:border-gray-700 p-12">
              <div className="flex justify-center">
                <div className="text-center">
                  <div className="w-12 h-12 border-4 border-gray-200 border-t-brand-600 rounded-full animate-spin mx-auto mb-4" />
                  <p className="text-gray-600 dark:text-gray-400">Loading documentation...</p>
                </div>
              </div>
            </div>
          ) : (
            <div className="lg:bg-surface lg:dark:bg-gray-870 lg:rounded-lg lg:shadow-sm lg:border lg:border-gray-100 lg:dark:border-gray-700 lg:p-8">
              <MarkdownRenderer content={content} docsRouteBase={docsConfig.routeBase} />
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
